import { Group, Stack, Title, Text, Collapse, Badge } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Card, ClickableText, Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { CertificateStatus } from "@tfg/types/constants/certificates.constants"

import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import { CertificateByOrganizationDto } from "~/gen/api"
import { useFindDocumentUrlQuery } from "~/hooks/useDocumentsQuery"
import { CertificateForm } from "~/pages/Brand/Certificates/components/CertificateForm.tsx"
import { CertificateRequestsValidationList } from "~/pages/Brand/Certificates/components/CertificateRequestsValidationList"
import { ProcessedCertificateDescription } from "~/pages/Brand/Certificates/components/ProcessedCertificateDescription"

type Props = {
  certificate: CertificateByOrganizationDto
  onClose: () => void
  opened: boolean
}

export const CertificateVerificationDrawer = ({
  certificate,
  onClose,
  opened,
}: Props) => {
  const { t } = useTranslation("certificates")

  const [certificateFormOpened, { toggle: toggleCertificateFormCollapse }] =
    useDisclosure(false)

  const { data: documentUrl, isPending: isLoading } = useFindDocumentUrlQuery(
    certificate.documentId,
    {
      enabled: !!certificate.documentId,
      refetchOnWindowFocus: false,
    }
  )

  const isCertificateTranscripted =
    CertificateStatus.PROCESSED === certificate.status

  if (!certificate.documentId || !documentUrl) {
    return null
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <CustomDrawer size="90%" h="100vh" onClose={onClose} opened={opened}>
      <Group h="100%" p={0} gap={0} align="start">
        <Stack
          w="45%"
          h="100%"
          p="md"
          bg="var(--mantine-color-brand-background)"
        >
          <iframe
            src={documentUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="Document Viewer"
          />
        </Stack>
        <Stack
          w="55%"
          mah="100%"
          bg="var(--mantine-color-brand-background)"
          p="md"
          h="100%"
          justify="start"
          align="start"
        >
          <Card
            w="100%"
            h={
              isCertificateTranscripted && !certificateFormOpened
                ? "fit-content"
                : "45%"
            }
          >
            <Card.Header>
              <Title order={3}>{t("modal.summary.card.title")}</Title>
            </Card.Header>
            {isCertificateTranscripted ? (
              <Card.Section>
                <ProcessedCertificateDescription certificate={certificate} />
                <ClickableText
                  onClick={toggleCertificateFormCollapse}
                  text={t("modal.summary.card.collapseButton.label")}
                />
              </Card.Section>
            ) : null}
            <Card.Section h="100%" style={{ overflowY: "scroll" }}>
              <Collapse
                in={!isCertificateTranscripted || certificateFormOpened}
              >
                <CertificateForm certificate={certificate} />
              </Collapse>
            </Card.Section>
          </Card>
          <Card
            w="100%"
            pos="relative"
            style={{ overflow: "scroll" }}
            pt={0}
            mah={
              isCertificateTranscripted && !certificateFormOpened
                ? "80%"
                : "55%"
            }
          >
            <Card.Header
              pos="sticky"
              bg="white"
              pt="md"
              pb="md"
              mb={0}
              top={0}
              left={0}
              right={0}
              style={{ zIndex: 1000 }}
            >
              <Stack gap="xs">
                <Group>
                  <Title order={3}>{t("modal.associatedRequests.title")}</Title>
                  <Badge color="blue">
                    {certificate.certificateRequests.length}
                  </Badge>
                </Group>
                <Text>
                  {t("modal.associatedRequests.description", {
                    sender: certificate.createdBy.organization.name,
                  })}
                </Text>
              </Stack>
            </Card.Header>

            <Card.Section mah="100%" style={{ overflow: "scroll" }} gap={0}>
              <CertificateRequestsValidationList certificate={certificate} />
            </Card.Section>
          </Card>
        </Stack>
      </Group>
    </CustomDrawer>
  )
}
